import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { api, ApiFailure } from "../lib/api";
import type { HealthResponse } from "@screener/contracts";

/**
 * 통합 포털 — auction.mycarebom.com 의 첫 화면.
 * 학습(낙찰로드)과 스크리너를 한 도메인에서 잇는 공개 입구다.
 */

const CARDS = [
  {
    href: "/study",
    external: true,
    eyebrow: "학습",
    title: "낙찰로드",
    body: "권리분석부터 입찰까지 단계별로 열리는 학습 코스와 실전 체크리스트.",
    cta: "학습 계속하기",
  },
  {
    href: "/properties",
    external: false,
    eyebrow: "실무",
    title: "경매 스크리너",
    body: "조건으로 물건을 걸러내고 하드필터 위험을 먼저 확인한다.",
    cta: "물건 걸러내기",
  },
] as const;

export default function Portal() {
  const [health, setHealth] = useState<HealthResponse | null>(null);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    let alive = true;
    api
      .health()
      .then((h) => alive && setHealth(h))
      .catch((e: unknown) =>
        alive
          ? setError(e instanceof ApiFailure ? e.message : "상태를 확인할 수 없다")
          : undefined,
      );
    return () => {
      alive = false;
    };
  }, []);

  return (
    <div className="py-6">
      <p className="text-[13px] font-semibold text-cobalt">경매 작업대</p>
      <h1 className="mt-2 text-[34px] font-extrabold leading-[1.15] tracking-tight sm:text-[44px]">
        배우는 곳과
        <br />
        걸러내는 곳을 한곳에서.
      </h1>
      <p className="mt-4 max-w-xl text-[15px] leading-relaxed text-ink-soft">
        학습은 낙찰로드에서, 물건 선별은 스크리너에서 합니다. 같은 계정으로
        오가며 물건에서 바로 체크리스트를 열 수 있습니다.
      </p>

      <div className="mt-10 grid gap-4 sm:grid-cols-2">
        {CARDS.map((c) => (
          <div
            key={c.title}
            className="flex flex-col rounded-[32px] border border-line bg-surface p-7"
          >
            <p className="text-[12px] font-bold uppercase tracking-wider text-ink-soft">
              {c.eyebrow}
            </p>
            <h2 className="mt-2 text-[22px] font-bold tracking-tight">{c.title}</h2>
            <p className="mt-3 flex-1 text-[14px] leading-relaxed text-ink-soft">
              {c.body}
            </p>
            {c.external ? (
              <a
                href={c.href}
                className="mt-6 inline-flex items-center justify-center rounded-full bg-ink px-6 py-3 text-[14px] font-semibold text-white transition hover:opacity-90"
              >
                {c.cta}
              </a>
            ) : (
              <Link
                to={c.href}
                className="mt-6 inline-flex items-center justify-center rounded-full bg-ink px-6 py-3 text-[14px] font-semibold text-white transition hover:opacity-90"
              >
                {c.cta}
              </Link>
            )}
          </div>
        ))}
      </div>

      <div className="mt-8 rounded-2xl border border-line px-4 py-3 text-[13px] text-ink-soft">
        {error ? (
          <span className="text-stop">API 상태 확인 실패 — {error}</span>
        ) : health ? (
          <span>
            API 정상 · {health.service} v{health.version} · 데이터 모드{" "}
            <strong className="font-semibold text-ink">{health.dataMode}</strong>
          </span>
        ) : (
          <span>API 상태 확인 중…</span>
        )}
      </div>
    </div>
  );
}
